import { type FocusEventHandler, type JSX, type ReactNode } from "react";

import { HtmlContent } from "@vega-core/components/HtmlContent/HtmlContent";

import { cnTwMerge } from "../../../../utils/cnTwMerge";
import { isTruthy } from "../../../../utils/isTruthy";
import { ValidationErrorText } from "../../../Form/Layout/ValidationError";
import { Checkbox } from "../../../shadcn/checkbox";

export enum CheckboxControlType {
  DEFAULT = "default",
  LIST = "list",
  PANEL = "panel",
  ACKNOWLEDGE = "acknowledge",
}

type Props = {
  checked: boolean;
  className?: string;
  disabled?: boolean;
  error?: string;
  label?: JSX.Element | ReactNode | string;
  name: string;
  onBlur: FocusEventHandler;
  onChange: (checked: boolean) => void;
  type?: CheckboxControlType;
  placeholder?: string;
  secondaryLabel?: string;
  withBackground?: boolean;
  content?: ReactNode;
  fullWidthContent?: boolean;
  fullWidth?: boolean;
};

export function CheckboxControl({
  checked,
  className,
  disabled,
  error,
  label,
  name,
  onBlur,
  content,
  onChange,
  fullWidthContent,
  secondaryLabel,
  type = CheckboxControlType.DEFAULT,
  withBackground,
  fullWidth = false,
}: Props) {
  return (
    <div
      className={cnTwMerge(
        "block",
        {
          "px-3 py-4": type === CheckboxControlType.PANEL && !fullWidth,
          "pl-3 py-4": type === CheckboxControlType.PANEL && fullWidth,
        },
        className,
      )}
      key={name}
    >
      <div
        className={cnTwMerge(
          cnTwMerge("flex items-start", {
            "bg-dark-gray p-4": withBackground,
          }),
        )}
      >
        <Checkbox
          checked={checked}
          className={cnTwMerge(
            "focus-visible:ring-2 focus-visible:ring-offset-1",
            {
              "mt-1":
                type === CheckboxControlType.ACKNOWLEDGE ||
                type === CheckboxControlType.PANEL,
            },
          )}
          data-testid={`${name}-checkbox`}
          disabled={disabled}
          id={name}
          name={name}
          onBlur={onBlur}
          onCheckedChange={onChange}
        />
        <div className="ml-3 flex-1 select-none">
          <label
            className={cnTwMerge(
              "block cursor-pointer body-base-medium text-text/secondary",
              {
                "pr-8": fullWidth,
                "body-base-regular text-text/primary":
                  type === CheckboxControlType.PANEL ||
                  type === CheckboxControlType.LIST,
                "body-base-regular": type === CheckboxControlType.ACKNOWLEDGE,
                "cursor-not-allowed opacity-50": disabled,
              },
            )}
            htmlFor={name}
          >
            <HtmlContent content={label} />
          </label>
          {isTruthy(secondaryLabel) && type === CheckboxControlType.PANEL ? (
            <p
              className="text-text/tertiary text-xs mt-1"
              id="comments-description"
            >
              {secondaryLabel}
            </p>
          ) : null}
          {isTruthy(content) && checked ? (
            <div
              className={cnTwMerge("mt-4", {
                "w-1/2": !fullWidthContent && !fullWidth,
                "w-full": fullWidth,
              })}
            >
              {content}
            </div>
          ) : null}
        </div>
      </div>
      {isTruthy(error) ? (
        <ValidationErrorText dataTestId={error} withMargin>
          {error}
        </ValidationErrorText>
      ) : null}
    </div>
  );
}
